// src/pages/Alerts.js
import React, { useState, useEffect } from 'react';
import { fetchDevices } from '../utils/deviceApi';
import { 
    fetchAlertRules, 
    addAlertRule, 
    deleteAlertRule, 
    fetchTriggeredAlerts, 
    acknowledgeTriggeredAlert,
    toggleAlertRule,
    acknowledgeAllTriggeredAlerts
} from '../utils/alertApi';
import { AlertCircle, Trash2, Plus, Check, Loader2, Bell, Filter, X, Search, Clock, Calendar, Play, Pause } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ✅ IMPROVED: Robust Time Logic
const timeAgo = (timestamp) => {
    if (!timestamp) return "N/A";
    const now = new Date();
    const alertTime = new Date(timestamp * 1000);
    const diffInSeconds = Math.floor((now - alertTime) / 1000);

    // Handle "Future" timestamps (server clock slightly ahead) or very recent
    if (diffInSeconds < 30) return "Just now";
    
    let interval = diffInSeconds / 31536000;
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = diffInSeconds / 2592000;
    if (interval > 1) return Math.floor(interval) + "mo ago";
    interval = diffInSeconds / 86400;
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = diffInSeconds / 3600;
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = diffInSeconds / 60;
    if (interval > 1) return Math.floor(interval) + "m ago";
    
    return Math.floor(diffInSeconds) + "s ago";
};

// Helper for exact date sub-text
const formatShortDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString([], { 
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
};

const Alerts = () => {
    // --- State ---
    const [rules, setRules] = useState([]);
    const [devices, setDevices] = useState([]);
    const [triggeredAlerts, setTriggeredAlerts] = useState([]);
    
    // UI States
    const [isLoadingRules, setIsLoadingRules] = useState(true);
    const [isLoadingAlerts, setIsLoadingAlerts] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);

    // Filter States
    const [filterDeviceId, setFilterDeviceId] = useState('ALL');
    const [filterStatus, setFilterStatus] = useState('ALL'); 
    const [searchTerm, setSearchTerm] = useState('');

    // Action Loading States
    const [isAddingRule, setIsAddingRule] = useState(false);
    const [isAckAllLoading, setIsAckAllLoading] = useState(false);
    const [deletingRuleId, setDeletingRuleId] = useState(null); 
    const [togglingRuleId, setTogglingRuleId] = useState(null); 
    const [acknowledgingAlertId, setAcknowledgingAlertId] = useState(null);

    const [formState, setFormState] = useState({
        device_id: 'ALL', metric: 'temperature', condition: '>', threshold: '', status_threshold: '', name: '', priority: 1,
    });

    // --- Load Data ---
    const loadData = async () => {
        setIsLoadingRules(true);
        setIsLoadingAlerts(true);
        setError('');
        try {
            const [fetchedRules, fetchedDevices, fetchedAlerts] = await Promise.all([
                fetchAlertRules(),
                fetchDevices(),
                fetchTriggeredAlerts(null, filterDeviceId === 'ALL' ? null : filterDeviceId), 
            ]);
            setRules(Array.isArray(fetchedRules) ? fetchedRules : []);
            setDevices(Array.isArray(fetchedDevices) ? fetchedDevices : []);
            setTriggeredAlerts(Array.isArray(fetchedAlerts) ? fetchedAlerts : []);
        } catch (err) {
            console.error("Load error:", err);
            setError('Failed to load data. Please refresh.');
        } finally {
            setIsLoadingRules(false);
            setIsLoadingAlerts(false);
        }
    };

    // --- Auto-Refresh ---
    useEffect(() => {
        loadData();
        const intervalId = setInterval(async () => {
            try {
                const freshAlerts = await fetchTriggeredAlerts(null, filterDeviceId === 'ALL' ? null : filterDeviceId);
                if (Array.isArray(freshAlerts)) {
                    setTriggeredAlerts(freshAlerts);
                }
            } catch (err) { console.error("Silent refresh failed", err); }
        }, 3000); 
        return () => clearInterval(intervalId);
    }, [filterDeviceId]);

    // --- Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => {
            const newState = { ...prev, [name]: value };
            if (name === 'metric') {
                if (value === 'status') {
                    newState.condition = '==';
                    newState.threshold = '';
                    newState.status_threshold = prev.status_threshold || ''; 
                } else {
                    newState.condition = '>';
                    newState.threshold = prev.threshold || ''; 
                    newState.status_threshold = '';
                }
            }
            return newState;
        });
    };

    const handleAddRule = async (e) => {
        e.preventDefault();
        setIsAddingRule(true);
        const ruleData = { ...formState, 
            threshold: formState.metric !== 'status' ? formState.threshold : null,
            status_threshold: formState.metric === 'status' ? formState.status_threshold : null,
            name: formState.name || `Rule for ${formState.metric}`
        };
        try {
            const result = await addAlertRule(ruleData);
            if (result && result.rules) {
                setRules(result.rules);
                setShowForm(false);
                setFormState({ device_id: 'ALL', metric: 'temperature', condition: '>', threshold: '', status_threshold: '', name: '', priority: 1 });
            }
        } catch { setError('Error adding rule.'); } 
        finally { setIsAddingRule(false); }
    };

    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm(`Delete rule ID ${ruleId}?`)) return;
        setDeletingRuleId(ruleId); 
        try {
            const result = await deleteAlertRule(ruleId);
            if (result && result.rules) setRules(result.rules);
        } finally { setDeletingRuleId(null); }
    };

    const handleToggleRule = async (ruleId, currentStatus) => {
        setTogglingRuleId(ruleId); 
        try {
            const result = await toggleAlertRule(ruleId, !currentStatus); 
            if (result && result.enabled !== undefined) {
                setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled: result.enabled } : r));
            }
        } finally { setTogglingRuleId(null); }
    };

    const handleAcknowledge = async (alertId) => {
        setAcknowledgingAlertId(alertId);
        try {
            const result = await acknowledgeTriggeredAlert(alertId);
            if (result) {
                const updated = await fetchTriggeredAlerts(null, filterDeviceId === 'ALL' ? null : filterDeviceId); 
                setTriggeredAlerts(Array.isArray(updated) ? updated : []);
            }
        } finally { setAcknowledgingAlertId(null); }
    };

    const handleAcknowledgeAll = async () => {
        if (!window.confirm(`Mark all unread alerts as read?`)) return;
        setIsAckAllLoading(true);
        try {
            const result = await acknowledgeAllTriggeredAlerts();
            if (result) {
                const updated = await fetchTriggeredAlerts();
                setTriggeredAlerts(Array.isArray(updated) ? updated : []);
            }
        } finally { setIsAckAllLoading(false); }
    };

    // Sorting & Filtering
    const processedAlerts = [...triggeredAlerts]
        .sort((a, b) => b.id - a.id)
        .filter(alert => {
            if (filterStatus === 'UNREAD') return !alert.acknowledged;
            if (filterStatus === 'READ') return alert.acknowledged;
            return true;
        })
        .filter(alert => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
                alert.id.toString().includes(term) ||
                alert.device_id.toLowerCase().includes(term) ||
                alert.metric.toLowerCase().includes(term)
            );
        });

    const unreadCount = triggeredAlerts.filter(a => !a.acknowledged).length;

    return (
        <div className="p-6 text-white min-h-screen font-sans">
            
            {/* --- Top Header & Stats --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold flex items-center gap-3 text-transparent bg-clip-text bg-gradient-to-r from-neonBlue to-white drop-shadow-lg">
                        <Bell className="text-neonPink animate-bounce" /> ALERT COMMAND
                    </h1>
                    <div className="flex items-center gap-2 mt-2 text-gray-400 text-sm">
                        <span className="flex items-center gap-1"><Calendar size={14}/> {new Date().toLocaleDateString()}</span>
                        <span>•</span>
                        <span className="text-neonBlue font-mono">Live Monitoring Active</span>
                    </div>
                </div>

                <div className="flex gap-4">
                     <div className="bg-gray-800/50 p-3 rounded-xl border border-gray-700 flex flex-col items-center min-w-[100px]">
                        <span className="text-xs text-gray-400 uppercase tracking-widest">Rules</span>
                        <span className="text-xl font-bold text-white">{rules.length}</span>
                     </div>
                     <div className={`p-3 rounded-xl border flex flex-col items-center min-w-[100px] transition-all ${unreadCount > 0 ? 'bg-neonPink/10 border-neonPink shadow-[0_0_15px_rgba(255,0,60,0.3)]' : 'bg-green-500/10 border-green-500'}`}>
                        <span className="text-xs uppercase tracking-widest text-white/80">Unread</span>
                        <span className={`text-xl font-bold ${unreadCount > 0 ? 'text-neonPink' : 'text-green-400'}`}>{unreadCount}</span>
                     </div>
                </div>
            </div>

            {error && <div className="bg-red-500/20 border border-red-500 text-red-100 p-4 rounded-lg mb-6 text-center backdrop-blur-md">{error}</div>}

            {/* --- Toolbar --- */}
            <div className="glass-card p-4 rounded-2xl mb-8 flex flex-col md:flex-row gap-4 justify-between items-center border border-gray-700/50">
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                    {/* Search */}
                    <div className="relative group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-neonBlue transition" size={18} />
                        <input 
                            type="text" 
                            placeholder="Search ID or Device..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-black/30 border border-gray-600 text-white pl-10 pr-4 py-2.5 rounded-xl focus:border-neonBlue focus:shadow-[0_0_10px_rgba(0,240,255,0.2)] outline-none w-full md:w-64 transition"
                        />
                    </div>
                    {/* Filter Device */}
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <select 
                            value={filterDeviceId} 
                            onChange={(e) => setFilterDeviceId(e.target.value)} 
                            className="bg-black/30 border border-gray-600 text-white pl-10 pr-8 py-2.5 rounded-xl focus:border-neonBlue outline-none appearance-none cursor-pointer hover:bg-black/50 transition w-full md:w-auto"
                        >
                            <option value="ALL">All Devices</option>
                            {devices.map((d) => (<option key={d.device_id} value={d.device_id}>{d.name || d.device_id}</option>))}
                        </select>
                    </div>
                    {/* Filter Status */}
                    <div className="bg-black/30 border border-gray-600 rounded-xl p-1 flex">
                        <button onClick={() => setFilterStatus('ALL')} className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${filterStatus === 'ALL' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}>All</button>
                        <button onClick={() => setFilterStatus('UNREAD')} className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${filterStatus === 'UNREAD' ? 'bg-neonPink text-black shadow-lg' : 'text-gray-400 hover:text-neonPink'}`}>Unread</button>
                    </div>
                </div>

                <div className="flex gap-3 w-full md:w-auto justify-end">
                    <button
                        onClick={() => setShowForm(!showForm)}
                        disabled={isAddingRule}
                        className={`px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition shadow-lg ${showForm ? 'bg-gray-700 text-white' : 'bg-neonBlue text-black hover:bg-white hover:shadow-neonBlue/50'}`}
                    >
                        {showForm ? <X size={18} /> : <Plus size={18} />}
                        {showForm ? 'Cancel' : 'New Rule'}
                    </button>
                </div>
            </div>

            {/* --- Add Rule Form --- */}
            <AnimatePresence>
                {showForm && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
                        <form onSubmit={handleAddRule} className="bg-gray-900/80 backdrop-blur-xl p-8 border border-neonBlue/30 rounded-2xl relative shadow-2xl">
                             <h3 className="text-xl font-bold text-neonBlue mb-6 border-b border-gray-700 pb-2">Configure New Trigger</h3>
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Device</label><select name="device_id" value={formState.device_id} onChange={handleInputChange} className="modern-input"><option value="ALL">Apply to ALL</option>{devices.map(d => <option key={d.device_id} value={d.device_id}>{d.name || d.device_id}</option>)}</select></div>
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Metric</label><select name="metric" value={formState.metric} onChange={handleInputChange} className="modern-input"><option value="temperature">Temperature</option><option value="humidity">Humidity</option><option value="pressure">Pressure</option><option value="status">Status</option></select></div>
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Condition</label><select name="condition" value={formState.condition} onChange={handleInputChange} className="modern-input">{formState.metric === 'status' ? (<><option value="==">Is Equal To</option><option value="!=">Is Not Equal To</option></>) : (<><option value=">">Greater Than</option><option value="<">Less Than</option></>)}</select></div>
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Threshold</label><input type={formState.metric === 'status' ? "text" : "number"} name={formState.metric === 'status' ? "status_threshold" : "threshold"} placeholder="Value" value={formState.metric === 'status' ? formState.status_threshold : formState.threshold} onChange={handleInputChange} required className="modern-input" /></div>
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Priority</label><input type="number" name="priority" value={formState.priority} onChange={handleInputChange} required className="modern-input" /></div>
                                <div className="space-y-2"><label className="text-xs text-gray-400 font-bold uppercase">Name</label><input type="text" name="name" placeholder="Optional" value={formState.name} onChange={handleInputChange} className="modern-input" /></div>
                            </div>
                            <button type="submit" disabled={isAddingRule} className="w-full bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold transition flex justify-center items-center gap-2 shadow-lg"> {isAddingRule ? <Loader2 className="animate-spin" /> : "Confirm Rule Configuration"} </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* --- ✅ RESTORED: Rules Table (User asked for this back!) --- */}
            <div className="glass-card mb-8 rounded-xl border border-gray-800 overflow-hidden">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">⚙️ Active Configuration Rules</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-800/50 text-gray-400 text-xs uppercase font-bold">
                            <tr>
                                <th className="p-4">Rule Name</th>
                                <th className="p-4">Target</th>
                                <th className="p-4">Condition</th>
                                <th className="p-4 text-center">Priority</th>
                                <th className="p-4 text-center">State</th>
                                <th className="p-4 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {rules.map(rule => (
                                <tr key={rule.id} className="hover:bg-gray-700/20 transition">
                                    <td className="p-4 font-bold text-gray-200">{rule.name}</td>
                                    <td className="p-4 text-sm text-neonBlue">{rule.device_id}</td>
                                    <td className="p-4 text-sm font-mono text-gray-400">{rule.metric} {rule.condition} {rule.threshold ?? rule.status_threshold}</td>
                                    <td className="p-4 text-center"><span className="bg-gray-700 text-white text-xs px-2 py-1 rounded">P{rule.priority}</span></td>
                                    
                                    {/* ✅ RESTORED: Pause/Enable Button */}
                                    <td className="p-4 text-center">
                                        <button 
                                            onClick={() => handleToggleRule(rule.id, rule.enabled)} 
                                            disabled={togglingRuleId === rule.id}
                                            className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1 mx-auto transition-all ${rule.enabled ? 'border-green-600 text-green-400 bg-green-900/20 hover:bg-red-900/20 hover:text-red-400 hover:border-red-600' : 'border-red-600 text-red-400 bg-red-900/20 hover:bg-green-900/20 hover:text-green-400 hover:border-green-600'}`}
                                        >
                                            {togglingRuleId === rule.id ? <Loader2 className="animate-spin w-3 h-3"/> : rule.enabled ? <><Pause size={12}/> ACTIVE</> : <><Play size={12}/> PAUSED</>}
                                        </button>
                                    </td>
                                    
                                    {/* Delete Button */}
                                    <td className="p-4 text-center">
                                        <button onClick={() => handleDeleteRule(rule.id)} disabled={deletingRuleId === rule.id} className="text-gray-500 hover:text-red-500 transition disabled:opacity-50">
                                            {deletingRuleId === rule.id ? <Loader2 className="animate-spin w-4 h-4"/> : <Trash2 size={16}/>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {rules.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-gray-500">No rules active.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- MAIN ALERT FEED --- */}
            <div className="glass-card rounded-2xl border border-gray-800 overflow-hidden shadow-2xl">
                 <div className="bg-gray-900/90 p-5 border-b border-gray-700 flex justify-between items-center sticky top-0 z-10 backdrop-blur-md">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="text-neonBlue" size={20} />
                        <h2 className="text-lg font-bold text-white">Live Alert Feed</h2>
                        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded-full border border-gray-700">{processedAlerts.length} Events</span>
                    </div>
                    {processedAlerts.some(a => !a.acknowledged) && (
                        <button onClick={handleAcknowledgeAll} disabled={isAckAllLoading} className="bg-yellow-600/10 text-yellow-400 hover:bg-yellow-600 hover:text-black border border-yellow-600/40 px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2">
                            {isAckAllLoading ? <Loader2 className="animate-spin w-3 h-3" /> : <Check size={14} />} MARK ALL READ
                        </button>
                    )}
                 </div>

                 <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800/50 text-gray-400 text-xs uppercase tracking-wider font-bold">
                            <tr>
                                <th className="p-5 text-center w-20">ID</th>
                                <th className="p-5">When</th>
                                <th className="p-5">Source Device</th>
                                <th className="p-5">Event Details</th>
                                <th className="p-5 text-center">Status</th>
                                <th className="p-5 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            <AnimatePresence>
                                {processedAlerts.map((alert) => (
                                    <motion.tr 
                                        key={alert.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        className={`group transition-all duration-300 ${
                                            !alert.acknowledged 
                                            ? 'bg-gradient-to-r from-neonPink/5 to-transparent hover:from-neonPink/10' 
                                            : 'hover:bg-gray-800/30'
                                        }`}
                                    >
                                        <td className="p-5 text-center align-middle">
                                            <span className={`font-mono font-bold text-sm px-2 py-1 rounded ${!alert.acknowledged ? 'bg-neonPink text-black' : 'text-gray-500 bg-gray-800'}`}>
                                                #{alert.id}
                                            </span>
                                        </td>
                                        
                                        {/* ✅ FIXED: Time Logic + Date Subtext */}
                                        <td className="p-5 align-middle">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <Clock size={14} className={!alert.acknowledged ? "text-neonPink" : "text-gray-600"} />
                                                    <span className={`text-sm font-bold ${!alert.acknowledged ? "text-white" : "text-gray-500"}`}>
                                                        {timeAgo(alert.timestamp)}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-600 mt-1 pl-6">
                                                    {formatShortDate(alert.timestamp)}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="p-5 font-bold text-white group-hover:text-neonBlue transition align-middle">{alert.device_id}</td>
                                        
                                        <td className="p-5 align-middle">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-400 text-sm uppercase text-[10px] tracking-wider">{alert.metric}</span>
                                                    <span className={`text-lg font-bold leading-none ${!alert.acknowledged ? 'text-neonPink drop-shadow-[0_0_5px_rgba(255,0,60,0.5)]' : 'text-gray-300'}`}>
                                                        {alert.trigger_value ?? alert.trigger_status}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-600 mt-1">Rule: {alert.condition} {alert.threshold ?? alert.status_threshold}</span>
                                            </div>
                                        </td>

                                        <td className="p-5 text-center align-middle">
                                            {alert.acknowledged ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-green-900/20 text-green-500 border border-green-900/50">
                                                    Resolved
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-red-900/20 text-neonPink border border-neonPink shadow-[0_0_10px_rgba(255,0,60,0.2)] animate-pulse">
                                                    <span className="w-2 h-2 rounded-full bg-neonPink"></span> Action Reqd
                                                </span>
                                            )}
                                        </td>

                                        {/* ✅ FIXED: Button Alignment */}
                                        <td className="p-5 text-center align-middle">
                                            {!alert.acknowledged && (
                                                <div className="flex justify-center">
                                                    <button 
                                                        onClick={() => handleAcknowledge(alert.id)} 
                                                        disabled={acknowledgingAlertId === alert.id}
                                                        className="w-10 h-10 rounded-xl bg-green-500 hover:bg-green-400 text-black flex items-center justify-center shadow-lg hover:shadow-green-400/50 transition-all transform hover:scale-105"
                                                        title="Mark as Resolved"
                                                    >
                                                        {acknowledgingAlertId === alert.id ? <Loader2 className="animate-spin w-5 h-5" /> : <Check size={20} strokeWidth={3} />}
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                            {processedAlerts.length === 0 && (
                                <tr><td colSpan="6" className="p-12 text-center text-gray-500 italic">No alerts found matching your filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .modern-input { @apply w-full p-3 rounded-xl bg-black/40 border border-gray-700 text-white focus:border-neonBlue focus:ring-1 focus:ring-neonBlue outline-none transition; }
            `}</style>
        </div>
    );
};

export default Alerts;